import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, Tabs, Empty } from 'antd'
import PageLoader from '../../../components/Layouts/PageLoader'
import MessageCard from './MessageCard'
import RulesPanel from './RulesPanel'
import ResultsPanel from './ResultsPanel'
import { KIND_ORDER } from './labels'
import { getRequest, dutyFollowUpMessagesUrl } from '../../../config'

// Follow-up messages (spec: duty-follow-up-messages): the four messages the assistant sends after a
// check — what they say, when and to whom, and whether they worked.
const FollowUpsPage = () => {
  const [tab, setTab] = useState('messages')
  const [loading, setLoading] = useState(false)
  const [messages, setMessages] = useState([])
  const dirty = useRef({})

  const load = async () => {
    const response = await getRequest(dutyFollowUpMessagesUrl, 'follow-up messages', setLoading)
    if (response.success) {
      const rows = response.data || []
      rows.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind))
      setMessages(rows)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const anyDirty = () => Object.values(dirty.current).some(Boolean)

  // Leaving the page with an unsaved message asks first.
  useEffect(() => {
    const warn = e => {
      if (!anyDirty()) return undefined
      e.preventDefault()
      e.returnValue = ''
      return ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [])

  const onDirtyChange = useCallback((kind, isDirty) => {
    dirty.current = { ...dirty.current, [kind]: isDirty }
  }, [])

  const onSaved = row =>
    setMessages(list => list.map(m => (m.kind === row.kind ? row : m)))

  const changeTab = key => {
    if (tab === 'messages' && anyDirty()) {
      Modal.confirm({
        title: 'You have unsaved changes',
        content: 'Leave this tab anyway? Your unsaved edits will be lost.',
        okText: 'Leave',
        onOk: () => {
          dirty.current = {}
          setTab(key)
        }
      })
      return
    }
    setTab(key)
  }

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 4 }}>Follow-up messages</h3>
          <p style={{ color: '#888', marginBottom: 12 }}>
            What the assistant sends after a check — each one kept short and sent once.
          </p>
          <Tabs activeKey={tab} onChange={changeTab}>
            <Tabs.TabPane tab="Messages" key="messages">
              {messages.length
                ? messages.map(message => (
                    <MessageCard
                      key={message.kind}
                      message={message}
                      onSaved={onSaved}
                      onDirtyChange={onDirtyChange}
                    />
                  ))
                : !loading && <Empty description="No follow-up messages found." />}
            </Tabs.TabPane>
            <Tabs.TabPane tab="When and to whom" key="rules">
              {tab === 'rules' && <RulesPanel />}
            </Tabs.TabPane>
            <Tabs.TabPane tab="Results" key="results">
              {tab === 'results' && <ResultsPanel />}
            </Tabs.TabPane>
          </Tabs>
        </div>
      </div>
    </PageLoader>
  )
}

export default FollowUpsPage
